import { ExpandedLinkElement, MentionElement, SlateNode, SlateUtils } from '@all-hands-hq/plex-graphql-schema';
import { useEffect } from 'react';
import { Descendant, Node, Transforms } from 'slate';
import { ReactEditor } from 'slate-react';

import { Entities, getEntity } from '../entities';
import { ReactEditorWithEntities } from './useSlate';

// We're updating this through an effect due to https://github.com/ianstormtaylor/slate/issues/3886
// which doesn't allow re-creating the editor on `entities` updates
export function useSyncEntitiesToEditor(editor: ReactEditorWithEntities, entities: Entities): void {
  useEffect(() => {
    editor.entitiesRef.current = entities;
  }, [editor, entities]);
}

// Taking `entitiesRef` instead of a value due to https://github.com/ianstormtaylor/slate/issues/3886
export const withEntities = (
  setEntities: (updateFn: (existing: Entities) => Entities) => void,
  entitiesRef: React.MutableRefObject<Entities>
) => (editor: ReactEditor): ReactEditorWithEntities => {
  const editorWithEntities = editor as ReactEditorWithEntities;

  editorWithEntities.entitiesRef = entitiesRef;
  editorWithEntities.setEntities = setEntities;

  const { getFragment, insertData } = editor;

  const withDataTransfers = SlateUtils.nodeMapper<ExpandedLinkElement | MentionElement>({
    match: (node: SlateNode): node is ExpandedLinkElement | MentionElement =>
      node.type === 'expanded_link' || node.type === 'mention',
    map: (node: ExpandedLinkElement | MentionElement) => {
      return {
        ...node,
        dataTransferEntity: getEntity(entitiesRef.current, node),
      };
    },
  });

  const withoutDataTransfers = SlateUtils.nodeMapper<ExpandedLinkElement | MentionElement>({
    match: (node: SlateNode): node is ExpandedLinkElement | MentionElement =>
      node.type === 'expanded_link' || node.type === 'mention',
    map: (node: ExpandedLinkElement | MentionElement): ExpandedLinkElement | MentionElement => {
      const { dataTransferEntity, ...rest } = node;

      editorWithEntities.setEntities((entities) => ({
        ...entities,
        [node.type]: [...entities[node.type], dataTransferEntity],
      }));

      return rest;
    },
  });

  // Augment elements which depend on entities with `dataTransferEntity`
  editor.getFragment = (): Descendant[] => {
    const fragment = getFragment() as SlateNode[];

    // TODO(https://github.com/all-hands-hq/source/issues/1046): shouldn't need as Descendent[] anymore
    return withDataTransfers(fragment) as Descendant[];
  };

  // Remove `dataTransferEntity` from elements and add it to entities
  editor.insertData = (data: DataTransfer): void => {
    const fragmentData = data.getData('application/x-slate-fragment');

    if (!!fragmentData) {
      // This is very similar to the default implementation of `insertData`, unfortunately we
      // need to replicate it here since we don't have a hook to modify `fragment` before it is inserted.
      const decoded = decodeURIComponent(window.atob(fragmentData));
      const fragment = JSON.parse(decoded) as SlateNode[];

      // TODO(https://github.com/all-hands-hq/source/issues/1046): shouldn't need as Node[] anymore
      Transforms.insertFragment(editor, withoutDataTransfers(fragment) as Node[]);
    } else {
      insertData(data);
    }
  };

  return editorWithEntities;
};
